import { z } from "zod";

import {
  type AssociationRecord,
  AssociationType,
} from "@/shared/domain/association";
import {
  type MessagePathPart,
  messagePathPartSchema,
  RepresentationStorageFormat,
} from "@/shared/domain/topics";

// ---------------------------------------------------------------------------
// TopicData is written out by hand so the application type names exactly the
// keys it carries. Inferring it from topicDataSchema below would pick up a
// string index signature from the topic object's .loose(), letting a misspelled
// key type-check anywhere TopicData is built or read.
// ---------------------------------------------------------------------------

export interface TopicData {
  topic: {
    id: string;
    name: string;
    association: AssociationRecord;
    startTime?: string | null;
    endTime?: string | null;
    messageCount?: number | null;
  };
  messagePath: {
    id: string;
    dotPath: string;
    metadata: Record<string, unknown>;
    parts: MessagePathPart[];
  };
  representation: {
    association: AssociationRecord;
    id: string;
    format: RepresentationStorageFormat;
  };
}

// ---------------------------------------------------------------------------
// Runtime validation for topic data read back out of a stored workspace
// configuration. The cast to z.ZodType<TopicData> keeps the topic object's
// index signature out of the parsed type.
// ---------------------------------------------------------------------------

const associationSchema: z.ZodType<AssociationRecord> = z.object({
  association_id: z.string(),
  association_type: z.enum([
    AssociationType.Dataset,
    AssociationType.File,
  ] as const),
  association_version: z.number().nullable().optional(),
});

const representationSchema = z.object({
  association: associationSchema,
  id: z.string(),
  format: z.enum(RepresentationStorageFormat),
});

export const topicDataSchema = z.object({
  topic: z
    .object({
      id: z.string(),
      name: z.string(),
      association: associationSchema,
      startTime: z.string().nullable().optional(),
      endTime: z.string().nullable().optional(),
      messageCount: z.number().nullable().optional(),
    })
    .loose(),
  messagePath: z.object({
    id: z.string(),
    dotPath: z.string(),
    metadata: z.record(z.string(), z.unknown()),
    parts: z.array(messagePathPartSchema),
  }),
  representation: representationSchema,
}) as z.ZodType<TopicData>;
